import { stripePromise, STRIPE_CONFIG, EscrowPaymentData, StripePaymentIntent } from './config';
import { API_URL } from '../../config/api';

class StripeService {
  private baseUrl = API_URL;

  /**
   * Créer un Payment Intent pour l'escrow
   */
  async createEscrowPaymentIntent(data: EscrowPaymentData): Promise<StripePaymentIntent> {
    try {
      // Mode simulation si le backend n'est pas disponible
      if (process.env.NODE_ENV === 'development') {
        console.log('Mode simulation activé - Backend non disponible');
        return this.simulatePayment(data);
      }

      const response = await fetch(`${this.baseUrl}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          currency: STRIPE_CONFIG.currency,
          platformFee: this.calculatePlatformFee(data.totalAmount),
          metadata: {
            bookingId: data.bookingId,
            ownerId: data.ownerId,
            renterId: data.renterId,
            dressTitle: data.dressTitle,
            type: 'escrow'
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const paymentIntent = await response.json();
      return paymentIntent;
    } catch (error) {
      console.error('Erreur lors de la création du Payment Intent:', error);
      console.log('Fallback vers mode simulation');
      return this.simulatePayment(data);
    }
  }

  /**
   * Confirmer le paiement avec Stripe Elements
   */
  async confirmPayment(clientSecret: string, paymentMethod: any) {
    try {
      // Mode simulation si le backend n'est pas disponible
      if (process.env.NODE_ENV === 'development' && clientSecret.includes('simulated')) {
        console.log('Mode simulation - Confirmation du paiement');
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              id: `pi_sim_${Date.now()}`,
              status: 'succeeded',
              amount: 10000, // 100€ en centimes
              currency: 'eur',
              metadata: {
                bookingId: 'booking_sim',
                type: 'escrow'
              }
            });
          }, 2000);
        });
      }

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe n\'est pas initialisé');
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod,
      });

      if (error) {
        throw new Error(error.message);
      }

      return paymentIntent;
    } catch (error) {
      console.error('Erreur lors de la confirmation du paiement:', error);
      throw error;
    }
  }

  /**
   * Libérer les fonds après confirmation mutuelle
   */
  async releaseEscrowFunds(paymentIntentId: string, ownerConfirmed: boolean, renterConfirmed: boolean) {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/release-funds`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId,
          ownerConfirmed,
          renterConfirmed,
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Erreur lors de la libération des fonds:', error);
      throw error;
    }
  }

  /**
   * Annuler un paiement et rembourser
   */
  async cancelPayment(paymentIntentId: string, reason: string) {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/cancel-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId,
          reason,
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Erreur lors de l\'annulation du paiement:', error);
      throw error;
    }
  }

  /**
   * Calculer les frais de la plateforme
   */
  private calculatePlatformFee(amount: number): number {
    const percentageFee = (amount * STRIPE_CONFIG.escrow.platformFeePercent) / 100;
    const totalFee = percentageFee + (STRIPE_CONFIG.escrow.fixedFeeCents / 100);
    return Math.round(totalFee * 100); // En centimes
  }

  /**
   * Simuler un paiement pour le développement
   */
  async simulatePayment(data: EscrowPaymentData): Promise<StripePaymentIntent> {
    // Simulation pour le développement
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          id: `pi_sim_${Date.now()}`,
          amount: data.totalAmount * 100, // En centimes
          currency: STRIPE_CONFIG.currency,
          status: 'requires_confirmation',
          client_secret: `pi_sim_${Date.now()}_secret_simulated`,
          metadata: {
            bookingId: data.bookingId,
            ownerId: data.ownerId,
            renterId: data.renterId,
            dressTitle: data.dressTitle,
          }
        });
      }, 1000);
    });
  }

  /**
   * Simuler la libération des fonds
   */
  async simulateReleaseFunds(paymentIntentId: string, ownerConfirmed: boolean, renterConfirmed: boolean) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          paymentIntentId,
          ownerConfirmed,
          renterConfirmed,
          fundsReleased: ownerConfirmed && renterConfirmed,
          timestamp: new Date().toISOString()
        });
      }, 2000);
    });
  }
}

export const stripeService = new StripeService();
export default stripeService;
